import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  UserPromotion,
  GetUserPromotionsResponse,
  DeletePromotionResponse,
  CreatePromotionRequest,
  CreatePromotionResponse,
  UpdatePromotionRequest,
  UpdatePromotionResponse
} from '@/types/promotions';
import { BELT_ORDER } from '@/constants/belt';

// Helper function to format promotion title
export const formatPromotionTitle = (promotion: UserPromotion): string => {
  if (promotion.stripe_count === 0) {
    return `${promotion.belt_color.charAt(0).toUpperCase() + promotion.belt_color.slice(1)} Belt`;
  }
  
  const stripeText = promotion.stripe_count === 1 ? '1st' : 
                    promotion.stripe_count === 2 ? '2nd' : 
                    promotion.stripe_count === 3 ? '3rd' : '4th';
  
  return `${stripeText} Stripe - ${promotion.belt_color.charAt(0).toUpperCase() + promotion.belt_color.slice(1)} Belt`;
};

// Helper function to sort promotions by Jiu-Jitsu hierarchy
export const sortPromotionsByHierarchy = (promotions: UserPromotion[]): UserPromotion[] => {
  return [...promotions].sort((a, b) => {
    const aIndex = BELT_ORDER.indexOf(a.belt_color);
    const bIndex = BELT_ORDER.indexOf(b.belt_color);
    
    if (aIndex !== bIndex) {
      return aIndex - bIndex; // Keep ascending order for belt hierarchy
    }
    
    // Same belt, sort by stripe count (ascending)
    if (a.stripe_count !== b.stripe_count) {
      return a.stripe_count - b.stripe_count;
    }
    
    // Same belt and stripe, sort by graduation date (ascending - oldest first)
    if (a.graduation_date && b.graduation_date) {
      return new Date(a.graduation_date).getTime() - new Date(b.graduation_date).getTime();
    }
    
    return 0;
  });
};

// Hook to get user promotions
export const useUserPromotions = (userId: string) => {
  return useQuery({
    queryKey: ['user-promotions', userId],
    queryFn: async (): Promise<UserPromotion[]> => {
      const { data, error } = await supabase.functions.invoke('get_user_promotions', {
        body: { user_id: userId }
      });

      if (error) {
        throw new Error(error.message);
      }

      // Handle case where response might be a string
      const response = typeof data === 'string' ? JSON.parse(data) : data;
      const responseData = response as GetUserPromotionsResponse;
      
      return responseData.data || [];
    },
    enabled: !!userId,
  });
};

// Hook to create promotion
export const useCreatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (promotionData: CreatePromotionRequest): Promise<UserPromotion> => {
      const { data, error } = await supabase.functions.invoke('create_user_promotion', {
        body: promotionData
      });

      if (error) {
        throw new Error(error.message);
      }

      const response = typeof data === 'string' ? JSON.parse(data) : data;
      const responseData = response as CreatePromotionResponse;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to create promotion');
      }

      return responseData.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
    },
  });
};

// Hook to update promotion
export const useUpdatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (promotionData: UpdatePromotionRequest): Promise<UserPromotion> => {
      const { data, error } = await supabase.functions.invoke('update_user_promotion', {
        body: promotionData
      });

      if (error) {
        throw new Error(error.message);
      }

      const response = typeof data === 'string' ? JSON.parse(data) : data;
      const responseData = response as UpdatePromotionResponse;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to update promotion');
      }

      return responseData.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
    },
  });
};

// Hook to delete promotion
export const useDeletePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (promotionId: string): Promise<void> => {
      const { data, error } = await supabase.functions.invoke('delete_user_promotion', {
        body: { promotion_id: promotionId }
      });

      if (error) {
        throw new Error(error.message || 'Failed to call delete function');
      }

      // The function should return { success: true, message: '...' } on success
      // If we got here without an error, consider it successful
      if (data && typeof data === 'object' && 'success' in data) {
        const response = data as DeletePromotionResponse;
        if (!response.success) {
          throw new Error('Failed to delete promotion');
        }
      }
      // If no specific success field, but no error, consider it successful
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
    },
  });
};